import { z } from 'zod';
import type { IRExpression, IRInstruction, IRStep } from './ir';

// IR Schemas
export const IRTypeSchema = z.enum(['int', 'text', 'bool']);

export const IRBaseNodeSchema = z.object({
  id: z.string(),
  loc: z
    .object({
      line: z.number(),
      col: z.number(),
    })
    .optional(),
});

export const IRExpressionSchema: z.ZodType<IRExpression> = z.lazy(() =>
  z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('literal'),
      type: IRTypeSchema,
      value: z.union([z.number(), z.string(), z.boolean()]),
    }),
    z.object({
      kind: z.literal('param_ref'),
      name: z.string(),
    }),
    z.object({
      kind: z.literal('counter_ref'),
      name: z.string(),
      binding: z.string().optional(),
    }),
    z.object({
      kind: z.literal('binop'),
      op: z.enum(['+', '-', '*', '/', '%', '==', '!=', '<', '<=', '>', '>=', 'and', 'or']),
      left: IRExpressionSchema,
      right: IRExpressionSchema,
    }),
    z.object({
      kind: z.literal('unop'),
      op: z.enum(['not', 'neg']),
      operand: IRExpressionSchema,
    }),
  ]),
);

export const IRParamSchema = IRBaseNodeSchema.extend({
  kind: z.literal('param'),
  name: z.string(),
  type: IRTypeSchema,
  default: IRExpressionSchema,
  label: z.string().optional(),
});

export const IRLinkSchema = z.object({
  kind: z.literal('link'),
  source: IRExpressionSchema,
  when: IRExpressionSchema.nullable(),
  delta: IRExpressionSchema,
});

export const IRCounterMutableSchema = IRBaseNodeSchema.extend({
  kind: z.literal('counter_mutable'),
  name: z.string(),
  init: IRExpressionSchema,
  links: z.array(IRLinkSchema),
});

export const IRCounterDerivedSchema = IRBaseNodeSchema.extend({
  kind: z.literal('counter_derived'),
  name: z.string(),
  expr: IRExpressionSchema,
});

export const IRCounterSchema = z.discriminatedUnion('kind', [
  IRCounterMutableSchema,
  IRCounterDerivedSchema,
]);

export const IRDeltaSchema = z.object({
  kind: z.literal('delta'),
  target: IRExpressionSchema,
  expr: IRExpressionSchema,
});

export const IRInstructionTextSchema = z.object({
  kind: z.literal('instruction_text'),
  expr: IRExpressionSchema,
});

// IRRow ↔ IRInstruction ↔ IRCase form a recursive cycle (cases nest rows that
// nest cases). z.lazy is used inside IRInstructionCasesSchema for the forward
// references to IRCaseSchema and IRRowSchema; everything else is defined in
// dependency order so the discriminated union can introspect the kind tag.
export const IRInstructionCasesSchema = z.object({
  kind: z.literal('instruction_cases'),
  cases: z.array(z.lazy(() => IRCaseSchema)),
  else: z.lazy(() => IRRowSchema).nullable(),
});

export const IRInstructionSchema: z.ZodType<IRInstruction> = z.discriminatedUnion('kind', [
  IRInstructionTextSchema,
  IRInstructionCasesSchema,
]);

export const IRRowSchema = IRBaseNodeSchema.extend({
  kind: z.literal('row'),
  instruction: IRInstructionSchema,
  deltas: z.array(IRDeltaSchema),
});

export const IRCaseSchema = z.object({
  when: IRExpressionSchema,
  row: IRRowSchema,
});

export const IRCountFixedSchema = z.object({
  kind: z.literal('count_fixed'),
  expr: IRExpressionSchema,
});

export const IRCountUntilTextSchema = z.object({
  kind: z.literal('count_until_text'),
  text_expr: IRExpressionSchema,
});

export const IRCountUntilPredSchema = z.object({
  kind: z.literal('count_until_pred'),
  predicate: IRExpressionSchema,
});

export const IRCountSchema = z.discriminatedUnion('kind', [
  IRCountFixedSchema,
  IRCountUntilTextSchema,
  IRCountUntilPredSchema,
]);

export const IRNoteSchema = IRBaseNodeSchema.extend({
  kind: z.literal('note'),
  text_expr: IRExpressionSchema,
});

export const IRGroupSchema = IRBaseNodeSchema.extend({
  kind: z.literal('group'),
  iter_name: z.string().nullable(),
  count: IRCountSchema,
  counters: z.array(IRCounterSchema),
  body: z.array(z.lazy(() => IRStepSchema)),
});

export const IRStepSchema: z.ZodType<IRStep> = z.lazy(() =>
  z.discriminatedUnion('kind', [IRRowSchema, IRGroupSchema, IRNoteSchema]),
);

export const IRSegmentSchema = IRBaseNodeSchema.extend({
  kind: z.literal('segment'),
  name: z.string(),
  counters: z.array(IRCounterSchema),
  setup: z.array(IRStepSchema),
  body: z.array(IRStepSchema),
});

// `ir_version` is informational metadata, not a gate. Old clients reading a
// future IR shape should still validate (extra fields are ignored, missing
// fields fall back to DSL re-parse via parseStateBlob); a strict literal
// here would defeat that. Bumps to the IR shape are documented in
// docs/architecture/storage-versioning.md.
export const IRPatternSchema = IRBaseNodeSchema.extend({
  kind: z.literal('pattern'),
  ir_version: z.string().optional(),
  name: z.string(),
  metadata: z.record(z.string(), z.string()).optional(),
  params: z.array(IRParamSchema),
  counters: z.array(IRCounterSchema),
  segments: z.array(IRSegmentSchema),
});

// Session Schemas (v2 wire format).
//
// Compact event tuple: [code, deltaMsFromStartedAt, arg]
//   codes: S=START P=PAUSE R=RESUME N=NEXT_ROW B=PREV_ROW X=EXPAND_NODE
//   arg is null except for X (carries nodeId).
export const CompactEventCodeSchema = z.enum(['S', 'P', 'R', 'N', 'B', 'X']);
export const CompactEventSchema = z.tuple([
  CompactEventCodeSchema,
  z.number().int().nonnegative(),
  z.string().nullable(),
]);

export const RowTimingSchema = z.tuple([
  z.number().int().nonnegative(),
  z.number().int().nonnegative(),
]);

export const SnapshotSchema = z.object({
  lastEventIndex: z.number().int(),
  currentRow: z.number().int().nonnegative(),
  totalTimeMs: z.number().nonnegative(),
  nodeExpansions: z.record(z.string(), z.number()).optional(),
});

// On-disk Session schema. The verbose `KnittingEvent[]` form lives only in memory;
// what hits IndexedDB and Gist is `history: CompactEvent[]`.
//
// `version` is a wire-only stamp written by the storage layer (see
// CURRENT_SESSION_VERSION). It is intentionally absent from the in-memory
// `Session` type so domain code can't branch on it.
export const SessionWireSchema = z.object({
  version: z.number().int().optional(),
  id: z.string(),
  patternId: z.string(),
  title: z.string(),
  status: z.enum(['idle', 'active', 'paused', 'completed', 'archived']),
  startedAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  snapshot: SnapshotSchema,
  history: z.array(CompactEventSchema),
  rowTimings: z.array(RowTimingSchema).optional(),
  totalTimeMs: z.number().nonnegative().optional(),
  paramValues: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])).optional(),
  compiledDsl: z.string().optional(),
  metadata: z.record(z.string(), z.string()).optional(),
});

// On-disk Pattern schema. Like SessionWireSchema, `version` is a wire-only
// stamp (see CURRENT_PATTERN_VERSION) absent from the in-memory `Pattern`.
export const PatternSchema = z.object({
  version: z.number().int().optional(),
  id: z.string(),
  title: z.string(),
  parentPatternId: z.string().nullable(),
  dsl: z.string(),
  // Serialized IRPattern JSON — when present, used directly instead of re-parsing the DSL.
  // Preserves node IDs across page reloads so EXPAND_NODE events remain valid.
  structureJson: z.string().optional(),
  sourceUrl: z.string().url().optional(),
  upstreamHash: z.string().optional(),
  builtin: z.boolean().optional(),
  js_source: z.string().optional(),
  // `color` is cosmetic. Validate as a plain string at the schema boundary
  // so a future-format value (e.g. `#rrggbbaa`, named colors) doesn't
  // quarantine the entire pattern. `toDomainPattern` enforces the hex format
  // and silently drops anything it doesn't recognize; `resolvePatternColor`
  // then falls back to `deriveColor(id)`.
  color: z.string().optional(),
});

// Schema is intentionally additive across `version` values: readers accept any
// numeric version (or none, for legacy blobs) and tolerate unknown fields written
// by future clients. Saves stamp the current version, but readers do not gate
// on it. Real breaking changes need a version bump AND a migration that accepts
// every prior version, not just N-1.
export const KnittingStateSchema = z.object({
  version: z.number().int().optional(),
  patterns: z.record(z.string(), PatternSchema),
  sessions: z.record(z.string(), SessionWireSchema),
});
